import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import {
  fetchInventoryItemDetail,
  getInventoryBarcode,
  getInventoryImageUrl,
  getInventoryOwnerContext,
  getInventoryPackLabel,
  getInventorySellingPrice,
  getInventoryStatus,
} from './inventoryApi';
import InventoryBarcodePreview from './InventoryBarcodePreview';
import './css/UserManagement.css';
import defaultFoodImage from './assets/DefaultFood.jpg';
import { defaultGroceryImage } from './groceryApi';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';

const INVENTORY_PATH = '/home?tab=inventory';

const displayValue = (value) => {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
};

function formatPrice(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `₹${n.toFixed(2)}`;
}

function formatDetailDate(v) {
  if (v == null || String(v).trim() === '') return '—';
  const s = String(v).trim().replace(' ', 'T');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(v);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}`;
}

function formatInventoryDate(v) {
  if (v == null || String(v).trim() === '') return '—';
  const text = String(v).trim();
  const datePart = text.includes('T') ? text.split('T')[0] : text.split(' ')[0];
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    if (year && month && day) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
  }
  const d = new Date(text.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return text;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const DetailField = ({ label, value, wide = false, children, labelAside = null }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className={`restaurant-detail-label${labelAside ? ' inventory-detail-label-with-action' : ''}`}>
      <span>{label}</span>
      {labelAside}
    </span>
    {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
  </div>
);

const InventoryItemDetail = () => {
  const { inventoryItemId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enabledTypes: enabledServiceTypes } = useRestaurantEnabledServices();
  const owner = getInventoryOwnerContext();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const detailTabs = useMemo(
    () => [
      { id: 'details', label: 'Item' },
      { id: 'stock', label: 'Stock' },
      { id: 'record', label: 'Record' },
    ],
    []
  );

  const requestedTab = searchParams.get('tab');
  const initialTab = detailTabs.some((t) => t.id === requestedTab) ? requestedTab : 'details';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (detailTabs.some((t) => t.id === requestedTab)) {
      setActiveTab(requestedTab);
    } else if (!requestedTab) {
      setActiveTab('details');
    }
  }, [requestedTab, detailTabs]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId === 'details') {
      next.delete('tab');
    } else {
      next.set('tab', tabId);
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    const rawId = inventoryItemId != null ? String(inventoryItemId).trim() : '';
    const idNum = Number(rawId);

    if (!rawId || Number.isNaN(idNum)) {
      setLoading(false);
      setError('Invalid inventory item id.');
      setRecord(null);
      return undefined;
    }

    (async () => {
      setLoading(true);
      setError('');
      setRecord(null);
      try {
        const data = await fetchInventoryItemDetail(idNum, owner);
        if (cancelled) return;
        setRecord(data);
        if (!data) {
          setError('No data returned for this item.');
        }
      } catch (e) {
        if (cancelled) return;
        console.error(
          'Inventory detail fetch failed:',
          e?.response?.status,
          e?.response?.data ?? e?.message
        );
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            'Failed to load inventory details.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inventoryItemId, owner.isStore, owner.storeId, owner.restaurantId]);

  const itemName = record?.itemName ?? record?.Item_Name ?? '';
  const title = itemName ? String(itemName).trim() : 'Inventory item';
  const imageUrl = record ? getInventoryImageUrl(record) : '';
  const inventoryDefaultImage = owner.isStore ? defaultGroceryImage : defaultFoodImage;
  const description = record?.description ?? record?.Description;
  const mfgDate = record?.mfgDate ?? record?.Mfg_Date;
  const expDate = record?.expDate ?? record?.Exp_Date;
  const status = record ? getInventoryStatus(record) : '';

  return (
    <RestaurantDashboardLayout activeTab="inventory" enabledServiceTypes={enabledServiceTypes}>
      <div className="inventory-item-detail-page">
        <div className="restaurant-detail-panel">
          <div className="restaurant-detail-card-header inventory-item-detail-card-header">
            <div className="inventory-item-detail-header-row">
              <div className="inventory-item-detail-header-main">
                <h3 className="restaurant-detail-section-title">{title}</h3>
                {!loading && !error && status ? (
                  <span
                    className={`user-status-pill user-status-pill--${
                      String(status).toUpperCase() === 'ACTIVE' ? 'active' : 'inactive'
                    }`}
                  >
                    {String(status).toUpperCase()}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                onClick={() => navigate(INVENTORY_PATH)}
                aria-label="Back to inventory"
              >
                <FaArrowLeft aria-hidden />
                <span>Back</span>
              </button>
            </div>
          </div>

          {loading ? <p className="order-details-loading">Loading inventory details…</p> : null}
          {!loading && error ? <p className="restaurant-detail-message error">{error}</p> : null}
          {!loading && !record && !error ? (
            <p className="order-details-empty">No detail data for this item.</p>
          ) : null}

          {!loading && !error && record ? (
            <div className="restaurant-detail-section">
              <div className="restaurant-detail-tabs" role="tablist" aria-label="Inventory item sections">
                <div className="restaurant-detail-tabs__list">
                  {detailTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      className={`restaurant-detail-tab${
                        activeTab === tab.id ? ' restaurant-detail-tab--active' : ''
                      }`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === 'details' ? (
                <div className="restaurant-detail-card" role="tabpanel">
                  <div className="restaurant-detail-card-header">
                    <h3 className="restaurant-detail-section-title">Item information</h3>
                  </div>
                  <div className="restaurant-detail-grid">
                    {owner.isStore ? (
                      <div className="restaurant-detail-field restaurant-detail-field-wide inventory-media-codes-field">
                        <InventoryBarcodePreview
                          barcode={getInventoryBarcode(record)}
                          itemName={itemName}
                          priceLabel={
                            getInventorySellingPrice(record) != null &&
                            getInventorySellingPrice(record) !== ''
                              ? formatPrice(getInventorySellingPrice(record))
                              : ''
                          }
                          imageUrl={imageUrl}
                          fallbackImage={inventoryDefaultImage}
                          isGroceryPlaceholder={!imageUrl}
                        />
                      </div>
                    ) : (
                      <DetailField label="Image">
                        <img
                          src={imageUrl || inventoryDefaultImage}
                          alt={title}
                          className="inventory-detail-item-image"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = inventoryDefaultImage;
                          }}
                        />
                      </DetailField>
                    )}
                    <DetailField
                      label="Item ID"
                      value={record.inventoryItemId ?? record.Inventory_Item_Id}
                    />
                    <DetailField label="Item name" value={itemName} />
                    <DetailField
                      label="Category"
                      value={record.groceryCategoryName ?? record.Grocery_Category_Name}
                    />
                    <DetailField label="Pack quantity" value={getInventoryPackLabel(record) || '—'} />
                    {owner.isStore ? (
                      <>
                        <DetailField label="MRP" value={formatPrice(record.mrp ?? record.MRP)} />
                        <DetailField
                          label="Selling price"
                          value={formatPrice(getInventorySellingPrice(record))}
                        />
                        <DetailField label="Status">
                          {status ? (
                            <span
                              className={`user-status-pill user-status-pill--${
                                String(status).toUpperCase() === 'ACTIVE' ? 'active' : 'inactive'
                              }`}
                            >
                              {String(status).toUpperCase()}
                            </span>
                          ) : (
                            <span className="restaurant-detail-value">—</span>
                          )}
                        </DetailField>
                      </>
                    ) : (
                      <DetailField
                        label="Unit price"
                        value={formatPrice(getInventorySellingPrice(record))}
                      />
                    )}
                    <DetailField label="Mfg date" value={formatInventoryDate(mfgDate)} />
                    <DetailField label="Exp date" value={formatInventoryDate(expDate)} />
                    <DetailField label="Description" value={description} wide />
                  </div>
                </div>
              ) : null}

              {activeTab === 'stock' ? (
                <div className="restaurant-detail-card" role="tabpanel">
                  <div className="restaurant-detail-card-header">
                    <h3 className="restaurant-detail-section-title">Stock levels</h3>
                  </div>
                  <div className="restaurant-detail-grid">
                    <DetailField
                      label="In stock"
                      value={record.quantityInStock ?? record.Quantity_In_Stock}
                    />
                    <DetailField
                      label="Reorder at"
                      value={record.reorderLevel ?? record.Reorder_Level}
                    />
                    <DetailField
                      label="Reorder quantity"
                      value={record.reorderQuantity ?? record.Reorder_Quantity}
                    />
                    <DetailField label="Comments" value={record.comments ?? record.Comments} wide />
                  </div>
                </div>
              ) : null}

              {activeTab === 'record' ? (
                <div className="restaurant-detail-card" role="tabpanel">
                  <div className="restaurant-detail-card-header">
                    <h3 className="restaurant-detail-section-title">Record details</h3>
                  </div>
                  <div className="restaurant-detail-grid">
                    <DetailField
                      label="Inventory level ID"
                      value={record.inventoryId ?? record.Inventory_Id ?? record.storeInventoryId}
                    />
                    <DetailField
                      label={owner.isStore ? 'Store ID' : 'Restaurant ID'}
                      value={
                        record.storeId ??
                        record.Store_Id ??
                        record.restaurantId ??
                        record.Restaurant_Id
                      }
                    />
                    <DetailField label="Created" value={formatDetailDate(record.createdDate)} />
                    <DetailField label="Created by ID" value={record.createdById} />
                    <DetailField label="Last updated" value={formatDetailDate(record.updatedDate)} />
                    <DetailField label="Updated by ID" value={record.updatedById} />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </RestaurantDashboardLayout>
  );
};

export default InventoryItemDetail;
